import React from 'react';
import defaultTo from 'lodash/defaultTo';
import { downloadQuotePdfV2, DownloadQuoteResponseDto, PDFStatus } from '@atlassian/temp-proxies';
import { AnalyticsTrackAction, AnalyticsTrackSubject, sendAnalyticsEvent } from '@atlassian/temp-analytics';
import { NotificationTypes, autoRetry, useNotification, errorNotificationBuilder } from '@css-xp/base';
import { NOTIFICATION_DISMISS_TIME, QUOTE_POLLING_INTERVAL, QUOTE_POLLING_MAX_TIMER } from '@css-xp/ppc-constants';
import { QuoteNotificationMsg } from '@css-xp/quotes';

export const useQuotePoller = (quoteId: string, txaId: string) => {
  const [isDownloadingPdf, setDownloadingPdf] = React.useState<boolean | undefined>(undefined);
  const [quoteFileName, setQuoteFileName] = React.useState<string | undefined>(undefined);

  const { sendErrorNotification } = useNotification();

  const errorNotificationOptions = errorNotificationBuilder()
    .withId('quote-error')
    .withDismissAfter(NOTIFICATION_DISMISS_TIME)
    .isVersion(NotificationTypes.FLAG);

  const shouldRetryDownloading = (pdfResponse: DownloadQuoteResponseDto | void): boolean => {
    return pdfResponse?.pdfStatus === PDFStatus.IN_PROGRESS ? true : false;
  };

  const doneDownloadingPdf = (pdfResponse: DownloadQuoteResponseDto) => {
    setDownloadingPdf(false);
    if (pdfResponse.pdfStatus === PDFStatus.NOT_FOUND) {
      const options = errorNotificationOptions
        .withHeader(QuoteNotificationMsg.PDF_NOT_FOUND)
        .withMessage('Pdf generation has not started. Please retry in some time')
        .build();
      sendErrorNotification(options);
    } else {
      if (pdfResponse.pdfUrl) {
        const anchorTag = document.createElement('a');
        anchorTag.href = pdfResponse.pdfUrl;
        anchorTag.download = defaultTo(quoteFileName, pdfResponse.pdfUrl);
        anchorTag.click();
        sendAnalyticsEvent(AnalyticsTrackAction.DOWNLOADED, AnalyticsTrackSubject.QUOTE_PDF, {
          transactionAccountId: txaId,
          quoteId,
        });
      }
    }
  };

  const handleQuoteV2Download = (fileName: string) => {
    setDownloadingPdf(true);
    setQuoteFileName(fileName);
    autoRetry({
      call: async () => downloadQuotePdfV2(quoteId, txaId),
      initialTimer: QUOTE_POLLING_INTERVAL, // poll every 5 seconds
      maxTimer: QUOTE_POLLING_MAX_TIMER, // max poll time 2 minutes
      shouldRetry: shouldRetryDownloading,
      onComplete: doneDownloadingPdf,
      numberOfTries: 100, // we can keep a higher number we dont want polling to stop
      backoffFactor: 2,
    });
  };

  return { isDownloadingPdf, handleQuoteV2Download };
};
